import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { type To, useSearchParams } from "react-router-dom";
import type { SubmissionError } from "redux-form";

import { Box, Stack, Typography } from "@braintrust/braintrust-ui-components";
import { resetGlobalAPIState } from "@js/api";
import { AuthComponentErrors, AuthEmojiTitle } from "@js/apps/auth/components";
import type { LoginFormData } from "@js/apps/auth/forms/login";
import { useWithRecaptcha } from "@js/apps/common/hooks";
import { getValuesWithReCaptchaCode } from "@js/forms/utils";
import {
  useAppDispatch,
  useNavigate,
  useQueryParams,
  useShowSnackbarFromQueryParam,
} from "@js/hooks";

import type { LoginProps } from "../../actions";
import { fetchCurrentUser, login, postUserLogin } from "../../actions";
import { AuthContainer } from "../../components/auth-container/auth-container";
import { EmailLoginForm } from "../../forms/login";
import {
  closeLoginMFAModal,
  openLoginMFAModal,
} from "../../forms/login/login-mfa-modal";
import { updateLocalStorageData } from "../../hooks/onboarding-form/helpers";
import type { LogInError } from "../../types";
import {
  isPostPublicApplicationLoginFlow,
  syncLoginRemoveWatcher,
  syncLoginSaveToLocalStorage,
  syncLoginWatchLocalStorage,
} from "../../utils";

export const AuthLoginPage = () => {
  const {
    message: messageCode,
    next,
    error_code: errorCode,
    invitation_key,
  } = useQueryParams() as Record<string, string | undefined>;
  const navigate = useNavigate();
  const isExtraAuthRequired =
    messageCode === ENUMS.AuthFailedErrorCodes.EXTRA_AUTH_REQUIRED;
  useWithRecaptcha();
  const dispatch = useAppDispatch();

  useShowSnackbarFromQueryParam();
  const initialValues: Partial<LoginFormData> = useMemo(() => {
    const loginFormInitialValues: Partial<LoginFormData> = {
      is_backup_code: false,
    };

    if (isExtraAuthRequired) {
      loginFormInitialValues.connect = true;
    }

    return loginFormInitialValues;
  }, [isExtraAuthRequired]);

  const handleLoginSuccess = useCallback(
    async (nextUrl?: string) => {
      dispatch(resetGlobalAPIState());
      syncLoginSaveToLocalStorage();

      const decodedUrl = nextUrl && decodeURIComponent(nextUrl);
      const shouldOpenURLWithTopLevelNavigation =
        decodedUrl &&
        (decodedUrl.includes("/api/confirm") ||
          decodedUrl.includes("/ai_interview/"));

      if (shouldOpenURLWithTopLevelNavigation) {
        window.open(nextUrl, "_self");
        return;
      }

      const user = await dispatch(fetchCurrentUser());
      await dispatch(postUserLogin(user));

      closeLoginMFAModal();

      const to: To =
        nextUrl && decodeURIComponent(nextUrl).includes("/auth/logout")
          ? "/"
          : nextUrl || "/";

      navigate(to);
    },
    [dispatch, navigate],
  );

  const onSubmitEmail = useCallback(
    async (values: LoginProps, nextUrl?: string) => {
      updateLocalStorageData();
      return await login(values)
        .then(() => handleLoginSuccess(nextUrl))
        .catch((exception: SubmissionError<unknown, string[]>) => {
          const errors = exception?.errors as LogInError | undefined;
          const otpType = errors?.otp_type?.[0];
          const requiresOtp =
            otpType === ENUMS.OTPVerificationMethod.EMAIL ||
            otpType === ENUMS.OTPVerificationMethod.TOTP ||
            errors?.otp_is_required;

          if (!errors || !requiresOtp) {
            throw exception;
          }

          openLoginMFAModal({
            errors,
            onPasskeySuccess: () => handleLoginSuccess(nextUrl),
          });
        });
    },
    [handleLoginSuccess],
  );

  const handleSubmit = useCallback(
    async (values: LoginFormData) => {
      const valuesWithReCaptchaCode = await getValuesWithReCaptchaCode(
        values,
        "login",
      );

      return onSubmitEmail(
        { ...valuesWithReCaptchaCode, invitation_key },
        next,
      );
    },
    [next, onSubmitEmail, invitation_key],
  );

  useEffect(() => {
    syncLoginWatchLocalStorage();
    return () => syncLoginRemoveWatcher();
  }, []);

  return (
    <AuthContainer pageTitle="Sign in">
      <AuthLoginFormWrapper errorCode={errorCode} messageCode={messageCode}>
        <EmailLoginForm
          onSubmit={handleSubmit}
          initialValues={initialValues}
          isExtraAuthRequired={isExtraAuthRequired}
        />
      </AuthLoginFormWrapper>
    </AuthContainer>
  );
};

type AuthLoginFormWrapperProps = {
  children: ReactNode;
  errorCode?: string;
  messageCode?: string;
};

const AuthLoginFormWrapper = ({
  children,
  errorCode,
  messageCode,
}: AuthLoginFormWrapperProps) => {
  const [params] = useSearchParams();
  if (isPostPublicApplicationLoginFlow(params)) {
    return (
      <Stack>
        <AuthEmojiTitle emoji="👋" />
        <Typography
          component="p"
          variant="paragraph"
          size="medium"
          sx={{ mb: 1 }}
        >
          Application submitted! We’ve saved your application to your account.
        </Typography>
        <Typography
          component="h1"
          variant="title"
          size="small"
          fontWeight={400}
          sx={{ mb: 1 }}
        >
          Sign in
        </Typography>
        <Typography
          component="p"
          variant="paragraph"
          size="large"
          fontWeight={400}
          sx={{ mb: 4 }}
        >
          Track your application, receive interview updates, and manage payments
          — all from your account. Sign in to continue.
        </Typography>

        <Box>
          <AuthComponentErrors
            messageCode={messageCode}
            errorCode={errorCode}
          />
          {children}
        </Box>
      </Stack>
    );
  }

  return (
    <Stack>
      <Typography
        component="h1"
        variant="title"
        size="small"
        fontWeight={400}
        sx={{ mb: 4 }}
      >
        Sign in
      </Typography>
      <AuthComponentErrors messageCode={messageCode} errorCode={errorCode} />
      {children}
    </Stack>
  );
};
